import { BAR_RANK_TRACK_ID } from './bar-rank'

// 复用当前图表的实际几何；只采集绘制路径，不改写系列或原始数据。
const palette = ['#24d8ff', '#39e2b1', '#78afff', '#ffc879']
const finite = value => typeof value === 'number' && Number.isFinite(value)
const number = value => value !== null && value !== '' && value !== undefined && Number.isFinite(Number(value))
const valueOf = value => value && typeof value === 'object' ? value.value : value
export function flowColor (value, fallback) {
  if (value && Array.isArray(value.colorStops)) value = (value.colorStops[0] || {}).color
  return typeof value === 'string' ? value : fallback
}
function collectPath (element) {
  if (!element || !element.shape || !element.buildPath) return null
  const commands = [], positions = []
  const add = (method, args) => {
    if (commands.length >= 6000) throw new Error('绘制路径超出动效预算')
    if (args.some(value => !finite(value))) return
    const mapped = []
    for (let at = 0; at < args.length; at += 2) {
      const point = element.transformCoordToGlobal ? element.transformCoordToGlobal(args[at], args[at + 1]) : args.slice(at, at + 2)
      if (!point.every(finite)) return
      mapped.push(...point); positions.push(point)
    }
    commands.push([method, mapped])
  }
  const recorder = {
    moveTo: (x, y) => add('moveTo', [x, y]), lineTo: (x, y) => add('lineTo', [x, y]),
    bezierCurveTo: (...args) => add('bezierCurveTo', args), quadraticCurveTo: (...args) => add('quadraticCurveTo', args),
    closePath: () => commands.push(['closePath', []])
  }
  try { element.buildPath(recorder, element.shape) } catch (_) { return null }
  return pathData(commands, positions)
}
function pathData (commands, positions) {
  if (!positions.length) return null
  const xs = positions.map(point => point[0]), ys = positions.map(point => point[1])
  let path
  if (typeof Path2D !== 'undefined') { path = new Path2D(); commands.forEach(([method, args]) => path[method](...args)) }
  return { commands, path, bounds: { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) } }
}
export function strokeFlowPath (ctx, shape) {
  if (shape.path) ctx.stroke(shape.path)
  else { ctx.beginPath(); shape.commands.forEach(([method, args]) => ctx[method](...args)); ctx.stroke() }
}
function projected (chart, series, index, option, horizontal) {
  const axis = (horizontal ? option.yAxis : option.xAxis) || [], categories = (axis[series[horizontal ? 'yAxisIndex' : 'xAxisIndex'] || 0] || {}).data || []
  return (series.data || []).slice(0, 2000).map((item, at) => {
    const raw = valueOf(item), category = categories[at] === undefined ? at : categories[at]
    if (!number(Array.isArray(raw) ? raw[horizontal ? 0 : 1] : raw)) return null
    const xy = Array.isArray(raw) ? raw : horizontal ? [raw, category] : [category, raw]
    try { const point = chart.convertToPixel({ seriesIndex: index }, xy); return Array.isArray(point) && point.length === 2 && point.every(finite) ? { point, category } : null } catch (_) { return null }
  })
}
function clipArea (model, width, height) {
  const cs = model && model.coordinateSystem, owner = cs && (cs.getRect ? cs : cs.grid || cs.master)
  const area = owner && owner.getRect ? owner.getRect() : null
  return area && [area.x, area.y, area.width, area.height].every(finite) ? { x: area.x, y: area.y, width: area.width, height: area.height } : { x: 0, y: 0, width, height }
}
function lineGeometry (chart, series, index, model, option, horizontal) {
  const view = model && chart.getViewOfSeriesModel && chart.getViewOfSeriesModel(model)
  const actual = view && collectPath(view._polyline)
  if (actual) return { shape: actual, lineWidth: view._polyline.style && view._polyline.style.lineWidth, pathColor: view._polyline.style && view._polyline.style.stroke }
  const points = projected(chart, series, index, option, horizontal), commands = [], positions = []; let open = false
  points.forEach(item => { if (!item) { open = false; return } commands.push([open ? 'lineTo' : 'moveTo', item.point]); positions.push(item.point); open = true })
  return { shape: pathData(commands, positions) }
}
function barGeometry (chart, series, index, model, option, horizontal, color, clip) {
  const data = model && model.getData(), projectedPoints = !data ? projected(chart, series, index, option, horizontal) : []
  const count = data ? data.count() : projectedPoints.length, rects = []
  for (let at = 0; at < Math.min(300, count); at++) {
    const element = data && data.getItemGraphicEl(at)
    let layout = element && element.shape || data && data.getItemLayout(at)
    if (!layout && projectedPoints[at]) {
      const item = projectedPoints[at], base = chart.convertToPixel({ seriesIndex: index }, horizontal ? [0, item.category] : [item.category, 0]), size = Math.min(30, (horizontal ? clip.height : clip.width) / Math.max(1, count) * .45)
      layout = horizontal ? { x: base[0], y: item.point[1] - size / 2, width: item.point[0] - base[0], height: size } : { x: item.point[0] - size / 2, y: base[1], width: size, height: item.point[1] - base[1] }
    }
    if (!layout || ![layout.x, layout.y, layout.width, layout.height].every(finite) || !layout.width || !layout.height) continue
    const itemColor = element && element.style && element.style.fill || data && data.getItemVisual(at, 'color')
    rects.push({ ...layout, color: flowColor(itemColor, color), index: at })
  }
  return { rects }
}
function pieGeometry (series, model, width, height, color) {
  const data = model && model.getData(), sectors = []
  if (data) for (let at = 0; at < Math.min(200, data.count()); at++) {
    const element = data.getItemGraphicEl(at), sector = element && element.childAt && element.childAt(0)
    const layout = sector && sector.shape || data.getItemLayout(at)
    if (layout && [layout.cx, layout.cy, layout.r, layout.r0, layout.startAngle, layout.endAngle].every(finite) && layout.r > 0 && Math.abs(layout.endAngle - layout.startAngle) > .0001) sectors.push({ ...layout, color: flowColor(sector && sector.style && sector.style.fill || data.getItemVisual(at, 'color'), color) })
  }
  else {
    const coordinate = (value, base, fallback) => typeof value === 'string' && value.endsWith('%') ? parseFloat(value) * base / 100 : finite(Number(value)) ? Number(value) : fallback
    const center = series.center || ['50%', '50%'], radii = Array.isArray(series.radius) ? series.radius : [0, series.radius || '70%']
    if ((series.data || []).some(item => number(valueOf(item)) && Number(valueOf(item)) > 0)) sectors.push({ cx: coordinate(center[0], width, width / 2), cy: coordinate(center[1], height, height / 2), r: coordinate(radii[1], Math.min(width, height) / 2, Math.min(width, height) * .35), r0: coordinate(radii[0], Math.min(width, height) / 2, 0), startAngle: -Math.PI / 2, endAngle: Math.PI * 1.5, color })
  }
  return { sectors }
}
export function chartFlowGeometry (vm) {
  const chart = vm.myChart, option = chart.getOption(), width = chart.getWidth(), height = chart.getHeight(), root = chart.getModel && chart.getModel()
  return (option.series || []).slice(0, 4).map((series, index) => {
    if (series.id === BAR_RANK_TRACK_ID) return null
    const model = root && root.getSeriesByIndex(index)
    if (model && root.isSeriesFiltered && root.isSeriesFiltered(model)) return null
    const horizontal = ((option.yAxis || [])[series.yAxisIndex || 0] || {}).type === 'category', clip = clipArea(model, width, height)
    const color = flowColor((series.lineStyle || {}).color || (series.itemStyle || {}).color || (option.color || [])[index], palette[index % palette.length])
    const base = { kind: series.type, color, horizontal, clip, index }
    if (series.type === 'line') return { ...base, ...lineGeometry(chart, series, index, model, option, horizontal) }
    if (series.type === 'bar') return { ...base, ...barGeometry(chart, series, index, model, option, horizontal, color, clip) }
    if (series.type === 'pie') return { ...base, ...pieGeometry(series, model, width, height, color) }
    return null
  }).filter(Boolean)
}
